/**
 * usePendingApprovalsBadge — devices waiting for approval under Security.
 *
 * A device that redeems a connect code waits until it's allowed (unless
 * it's set to Always allow), so the sidebar shows how many are waiting.
 * Same rhythm as useCommunicationBadge: a slow poll plus a refresh when
 * the window regains focus; a failed request counts as zero.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { sessionsApi } from '@/features/auth/api/sessionsApi';

const POLL_INTERVAL_MS = 60_000;

export function usePendingApprovalsBadge(): number {
    const [count, setCount] = useState(0);
    const inFlightRef = useRef(false);

    const refresh = useCallback(async () => {
        if (inFlightRef.current) return;
        inFlightRef.current = true;
        try {
            const { sessions } = await sessionsApi.list();
            setCount(sessions.filter((session) => session.status === 'pending').length);
        } catch {
            setCount(0);
        } finally {
            inFlightRef.current = false;
        }
    }, []);

    useEffect(() => {
        // The first count comes from a timer too, so nothing is set while the effect itself runs.
        const first = window.setTimeout(() => void refresh(), 0);
        const id = window.setInterval(() => void refresh(), POLL_INTERVAL_MS);
        const onFocus = () => void refresh();
        window.addEventListener('focus', onFocus);
        return () => {
            window.clearTimeout(first);
            window.clearInterval(id);
            window.removeEventListener('focus', onFocus);
        };
    }, [refresh]);

    return count;
}
